/* eslint camelcase: 0 */
import { XIcon } from '@heroicons/react/solid';
import { ExtendedCollaborator, MutationSetCollaboratorsArgs, User } from 'lib/generated/graphql';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import Carousel from 'src/screens/onboarding/components/Carousel';
import { Button } from 'src/shared-components';
import { AddUserByEmail, SearchForUser } from './FormSearch';

// inside form display component
interface DisplayBoxProps {
  name?: string;
  photoUrl?: string;
  onClick?: () => void;
  isHuddleUser?: boolean;
  email?: string;
  deleteButton?: boolean;
}

const DisplayBox = ({ name, photoUrl, onClick, isHuddleUser = true, email, deleteButton = true }: DisplayBoxProps) => {
  return (
    <div className="text-[#595959] border-2 rounded-lg py-2 border-[#EBEBEB] flex items-center gap-x-4">
      <div className="flex ml-4">
        {isHuddleUser ? (
          <Image className="rounded" height={24} width={24} src={photoUrl || process.env.DEFAULT_USER_AVATAR} />
        ) : (
          '📧'
        )}
      </div>
      <div>{isHuddleUser ? name : email}</div>
      {deleteButton ? (
        <div onClick={onClick} className="ml-auto mr-5 cursor-pointer">
          <XIcon className="h-5 w-5" />
        </div>
      ) : null}
    </div>
  );
};

const carouselData = [
  {
    position: 0,
    title: 'Add your favorite collaborators',
    hideStep: true,
  },
  {
    position: 1,
    title: "Some of your collaborators aren't in the huddle community yet",
    hideStep: true,
  },
];

// ***MAIN COMPONENT
interface CollaboratorsFormProps {
  collaboratorsData: ExtendedCollaborator[];
  user: User;
  handleSubmitCollaborators: (
    userId: MutationSetCollaboratorsArgs['userId'],
    data: MutationSetCollaboratorsArgs['data'],
    closeModal: boolean,
  ) => void;
  sendEmails: (emails: string[]) => void;
}

const CollaboratorsForm = ({
  collaboratorsData = [],
  handleSubmitCollaborators,
  user,
  sendEmails,
}: CollaboratorsFormProps) => {
  // set initial data, should it be []?
  useEffect(() => {
    setHuddleCollaborators(collaboratorsData?.map(dataObj => dataObj.collaborator) || []);
    setTotalData(collaboratorsData?.map(dataObj => dataObj.collaborator) || []);
  }, []);

  // setting total data helps to display next field on top regardless of type of data
  const [huddleCollaborators, setHuddleCollaborators] = useState([]);
  const [emails, setEmails] = useState([]);
  const [totalData, setTotalData] = useState([]);

  // data updates
  const addCollaborator = (collaborator: User) => {
    const { id, name, email, username, photoUrl } = collaborator;
    const collaboratorObj = { id, name, email, username, photoUrl };
    const test = huddleCollaborators.filter(collaborator => collaborator.id === id);
    if (!test.length) {
      setHuddleCollaborators([collaboratorObj, ...huddleCollaborators]);
      setTotalData([collaboratorObj, ...totalData]);
    }
  };

  const deleteCollaborator = (collaboratorId: number) => {
    setHuddleCollaborators(huddleCollaborators.filter(collaborator => collaborator.id !== collaboratorId));
    setTotalData(
      totalData.filter(collaborator => {
        if (typeof collaborator === 'object') return collaborator.id !== collaboratorId;
        else return true;
      }),
    );
  };

  const addEmail = (inputEmail: string) => {
    const test = emails.filter(email => inputEmail === email);
    if (!test.length) {
      setEmails([inputEmail, ...emails]);
      setTotalData([inputEmail, ...totalData]);
    }
  };

  const deleteEmail = (inputEmail: string) => {
    setEmails(emails.filter(email => email !== inputEmail));
    setTotalData(
      totalData.filter(email => {
        if (typeof email === 'string') return email !== inputEmail;
        else return true;
      }),
    );
  };

  // display transitions & data
  const [step, setStep] = useState<number>(0);

  const submitData = async () => {
    const { id: userId } = user;
    const dataArray = huddleCollaborators.map(collaborator => ({ userId, collaboratorId: collaborator.id }));
    const closeModal = !emails.length;
    handleSubmitCollaborators(userId, dataArray, closeModal);
    if (!closeModal) setStep(1);
  };

  const submitEmails = () => {
    sendEmails(emails);
  };

  // helper functions to deal w/ mix of data types
  const display = data => {
    if (typeof data === 'string')
      return <DisplayBox isHuddleUser={false} key={data} email={data} onClick={() => deleteEmail(data)} />;
    else {
      const { id, name, photoUrl } = data;
      return (
        <DisplayBox key={id} name={name} photoUrl={photoUrl} isHuddleUser onClick={() => deleteCollaborator(id)} />
      );
    }
  };

  return (
    <Carousel currentStep={step} steps={carouselData} className="sm:w-[450px] overflow-auto">
      {step === 0 && (
        <div className="w-full flex flex-col gap-y-3 px-2">
          <SearchForUser onSelectUser={addCollaborator} />
          <AddUserByEmail addEmail={addEmail} />
          <div className="flex flex-col gap-y-3 min-h-[200px]">{totalData.map(data => display(data))}</div>
          <Button fullWidth color="black" size="lg" onClick={submitData}>
            Save
          </Button>
        </div>
      )}
      {step === 1 && (
        <div className="w-full flex flex-col gap-y-3 px-2">
          {emails.map(email => {
            return <DisplayBox isHuddleUser={false} key={email} email={email} deleteButton={false} />;
          })}
          <div className="px-6">{"In order to add them , you'll need to invite them to join Huddle."}</div>
          <Button fullWidth color="black" size="lg" onClick={submitEmails}>
            Confirm - send invites
          </Button>
          <div className="cursor-pointer" onClick={() => setStep(0)}>
            Go back to previous step
          </div>
        </div>
      )}
    </Carousel>
  );
};

export default CollaboratorsForm;
